import React, { useState, useEffect, useRef } from "react";
import VialImage from "./VialImage";

// White-removal canvas function: strips background, keeps reflections
function processVial(imgSrc, callback) {
  const img = new Image();
  img.onload = function() {
    const c = document.createElement("canvas");
    c.width = img.width; c.height = img.height;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const d = ctx.getImageData(0, 0, c.width, c.height);
    const px = d.data;
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i], g = px[i+1], b = px[i+2];
      if (r > 220 && g > 220 && b > 220) {
        const w = Math.min(r, g, b);
        if (w > 245) px[i+3] = 0;
        else px[i+3] = Math.floor((255 - w) * 10);
      }
    }
    ctx.putImageData(d, 0, 0);
    callback(c.toDataURL("image/png"));
  };
  img.onerror = () => callback(imgSrc);
  img.src = imgSrc;
}

// Sale helpers — silently treat missing/malformed salePrice as no sale.
function ppIsOnSale(v) {
  if (!v) return false;
  const reg = Number(v.price) || 0;
  const sale = v.salePrice != null ? Number(v.salePrice) : null;
  return sale != null && Number.isFinite(sale) && sale >= 0 && sale < reg;
}
function ppEffective(v) {
  if (!v) return 0;
  const reg = Number(v.price) || 0;
  const sale = v.salePrice != null ? Number(v.salePrice) : null;
  return (sale != null && Number.isFinite(sale) && sale >= 0 && sale < reg) ? sale : reg;
}
function ppHasSale(product) {
  if (!product || !product.variants) return false;
  return product.variants.some(ppIsOnSale);
}

export default function ProductPage({ product, allProducts, onBack, onAddToCart, onCoaPending }) {
  const [selectedMg, setSelectedMg] = useState(product.variants[0].mg);
  const [packIndex, setPackIndex] = useState(-1); // -1 = single
  const [qty, setQty] = useState(1);
  const [transparentImages, setTransparentImages] = useState({});
  const [zoomOpen, setZoomOpen] = useState(false);
  const [imgChanging, setImgChanging] = useState(false);

  // Map source URLs to pre-processed clean PNG URLs (static files)
  useEffect(() => {
    const variantImgs = product.variantImages || {};
    const sizes = Object.keys(variantImgs);
    const cleanMap = {};
    sizes.forEach(mg => {
      const src = variantImgs[mg];
      // Convert /vials/foo.webp -> /vials-clean/foo.png
      cleanMap[mg] = src.replace("/vials/", "/vials-clean/").replace(".webp", ".png");
    });
    setTransparentImages(cleanMap);
  }, [product]);

  // Reset when product changes
  useEffect(() => {
    setSelectedMg(product.variants[0].mg);
    setPackIndex(-1);
    setQty(1);
  }, [product.id]);

  const currentVariant = product.variants.find(v => v.mg === selectedMg) || product.variants[0];
  const outOfStock = currentVariant && currentVariant.inStock === false;
  const isSale = ppIsOnSale(currentVariant);
  const basePrice = ppEffective(currentVariant);
  const regularBasePrice = Number(currentVariant.price) || 0;
  const pack = packIndex >= 0 ? product.packs[packIndex] : null;
  const mult = pack ? pack.mult : 1;
  const discount = pack ? pack.save / 100 : 0;
  const totalBeforeDiscount = basePrice * mult * qty;
  const totalAfterDiscount = totalBeforeDiscount * (1 - discount);
  const regularTotalAfterDiscount = regularBasePrice * mult * qty * (1 - discount);
  const savings = totalBeforeDiscount - totalAfterDiscount;

  // Related products (other products from the array)
  const related = (allProducts || []).filter(p => p.id !== product.id).slice(0, 4);

  const fmt = (n) => {
    const s = n.toFixed(2);
    return s.endsWith(".00") ? s.slice(0,-3) : s;
  };

  const transparentImg = transparentImages[selectedMg];
  const originalImg = product.variantImages?.[selectedMg] || product.image;

  function selectSize(mg) {
    setImgChanging(true);
    setTimeout(() => {
      setSelectedMg(mg);
      setImgChanging(false);
    }, 150);
  }

  function handleAdd() {
    const mi = product.variants.findIndex(v => v.mg === selectedMg);
    onAddToCart(product, mi, packIndex, qty);
  }

  return (
    <div style={pageStyle}>
      <style>{`
        @keyframes mol-enter { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        @keyframes bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-25px); } }
        @keyframes vial-enter { 0% { opacity: 0; transform: translateY(-25px) scale(0.9); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes text-in { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bs-ray { 0% { left: -100%; } 60% { left: 130%; } 100% { left: 130%; } }
        
        .rr-mol-bg { animation: mol-enter 1s cubic-bezier(0.34,1.4,0.64,1) 0.1s forwards, bob 6s ease-in-out 1.2s infinite; opacity: 0; }
        .rr-vial-enter { animation: vial-enter 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.3s forwards; opacity: 0; }
        .rr-best-tag, .rr-zoom-btn { animation: vial-enter 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.4s forwards; opacity: 0; }
        .rr-thumbs-row { animation: vial-enter 0.8s ease-out 0.6s forwards; opacity: 0; }
        .rr-info-col > * { opacity: 0; animation: text-in 0.6s ease-out forwards; }
        .rr-info-col > *:nth-child(1) { animation-delay: 0.5s; }
        .rr-info-col > *:nth-child(2) { animation-delay: 0.55s; }
        .rr-info-col > *:nth-child(3) { animation-delay: 0.6s; }
        .rr-info-col > *:nth-child(4) { animation-delay: 0.65s; }
        .rr-info-col > *:nth-child(5) { animation-delay: 0.7s; }
        .rr-info-col > *:nth-child(6) { animation-delay: 0.75s; }
        .rr-info-col > *:nth-child(7) { animation-delay: 0.8s; }
        .rr-info-col > *:nth-child(8) { animation-delay: 0.85s; }
        .rr-info-col > *:nth-child(9) { animation-delay: 0.9s; }
        .rr-info-col > *:nth-child(10) { animation-delay: 0.95s; }
        .rr-info-col > *:nth-child(11) { animation-delay: 1s; }
        .rr-info-col > *:nth-child(12) { animation-delay: 1.05s; }
        
        .rr-best-tag::before, .rr-popular-tag::before {
          content: ""; position: absolute; top: -50%; left: -100%;
          width: 30%; height: 200%;
          background: linear-gradient(90deg, transparent, rgba(255,245,200,0.9), transparent);
          transform: rotate(25deg); animation: bs-ray 2.5s ease-in-out infinite;
          pointer-events: none;
        }
        
        .rr-grid-2 { display: grid; grid-template-columns: 1fr; gap: 0; }
        @media (min-width: 900px) { .rr-grid-2 { grid-template-columns: 1.1fr 1fr; gap: 50px; } }
        
        .rr-rel-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; max-width: 100%; place-content: center; place-items: stretch; }
        @media (min-width: 768px) { .rr-rel-grid { grid-template-columns: repeat(4,1fr); gap: 18px; } }
        
        .rr-rel-card:hover { border-color: #111 !important; transform: translateY(-3px); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }
        .rr-opt:hover { border-color: #111 !important; }
        .rr-thumb-img { width: 60px; height: 60px; object-fit: contain; }
      `}</style>

      {/* Back nav */}
      <div style={crumbStyle}>
        <span onClick={onBack} style={{cursor:"pointer", color:"#555"}}>← Back</span>
        <span style={{margin:"0 6px", color:"#bbb"}}>›</span>
        <span style={{color:"#111", fontWeight:600}}>{product.code}</span>
      </div>

      {/* Product section with molecules */}
      <div style={productSectionStyle}>
        <div className="rr-mol-bg" style={molBgStyle}>
          <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" style={{width:"100%", height:"100%"}}>
            <g fill="#c9962f" stroke="#c9962f" strokeWidth="1.5" opacity="0.22">
              <circle cx="40" cy="40" r="7"/><circle cx="110" cy="20" r="6"/><line x1="40" y1="40" x2="110" y2="20" strokeWidth="1.2"/>
              <circle cx="110" cy="20" r="6"/><circle cx="180" cy="60" r="7"/><line x1="110" y1="20" x2="180" y2="60"/>
              <circle cx="50" cy="360" r="7"/><circle cx="130" cy="380" r="6"/><line x1="50" y1="360" x2="130" y2="380" strokeWidth="1.2"/>
              <circle cx="130" cy="380" r="6"/><circle cx="200" cy="350" r="7"/><line x1="130" y1="380" x2="200" y2="350"/>
              <circle cx="500" cy="40" r="8"/><circle cx="580" cy="80" r="6"/><line x1="500" y1="40" x2="580" y2="80"/>
              <circle cx="580" cy="80" r="6"/><circle cx="660" cy="40" r="8"/><line x1="580" y1="80" x2="660" y2="40"/>
              <circle cx="700" cy="120" r="7"/><circle cx="760" cy="180" r="8"/><line x1="700" y1="120" x2="760" y2="180"/>
              <circle cx="600" cy="220" r="7"/><circle cx="680" cy="260" r="6"/><line x1="600" y1="220" x2="680" y2="260" strokeWidth="1.2"/>
              <circle cx="680" cy="260" r="6"/><circle cx="750" cy="310" r="8"/><line x1="680" y1="260" x2="750" y2="310"/>
              <circle cx="520" cy="340" r="7"/><circle cx="590" cy="370" r="6"/><line x1="520" y1="340" x2="590" y2="370"/>
              <circle cx="430" cy="180" r="7"/><circle cx="480" cy="120" r="6"/><line x1="430" y1="180" x2="480" y2="120"/>
            </g>
          </svg>
        </div>

        <div className="rr-grid-2">
          {/* IMAGE COLUMN */}
          <div style={imageColStyle}>
            {product.popular && <span className="rr-best-tag" style={bestTagStyle}>BEST SELLER</span>}
            <button className="rr-zoom-btn" style={zoomBtnStyle} onClick={() => setZoomOpen(true)}>🔍</button>
            <div className="rr-vial-enter" style={{...mainImgWrapStyle, opacity: imgChanging ? 0.3 : 1, transition:"opacity 0.3s ease"}} onClick={() => setZoomOpen(true)}>
              {transparentImg ? (
                <img src={transparentImg} alt={product.name} style={mainImgStyle}/>
              ) : (
                <div style={{width:200,height:300}}/>
              )}
            </div>
            <div className="rr-thumbs-row" style={thumbsRowStyle}>
              {product.variants.map(v => (
                <div key={v.mg} onClick={() => selectSize(v.mg)} style={{...thumbStyle, borderBottomColor: selectedMg === v.mg ? "#111" : "transparent"}}>
                  {transparentImages[v.mg] && <img src={transparentImages[v.mg]} className="rr-thumb-img" alt={v.mg}/>}
                  <span style={{...thumbLabelStyle, color: selectedMg === v.mg ? "#111" : "#888"}}>{v.mg}</span>
                </div>
              ))}
            </div>
          </div>

          {/* INFO COLUMN */}
          <div className="rr-info-col" style={infoColStyle}>
            <div style={badgesRowStyle}>
              {product.popular && <span className="rr-popular-tag" style={pillBestStyle}>Best Seller</span>}
              {product.purity && <span style={pillPurityStyle}>99%+ Pure</span>}
              {product.lowStock && <span style={pillStockStyle}>Low Stock</span>}
              {ppHasSale(product) && <span style={pillSaleStyle}>🔥 SALE</span>}
            </div>
            <p style={codeStyle}>Product Code</p>
            <h1 style={titleStyle}>{product.code}</h1>
            <p style={subtitleStyle}>For In Vitro Research Use Only — Not for Human or Veterinary Application</p>
            <p style={priceStyle}>
              {isSale ? (<>
                <s style={{color:"#888",fontSize:24,fontWeight:600,marginRight:10}}>${fmt(regularTotalAfterDiscount)}</s>
                <span style={{color:"#c9962f"}}>${fmt(totalAfterDiscount)}</span>
              </>) : (<>${fmt(totalAfterDiscount)}</>)}
              <small style={priceSmallStyle}>/ {mult > 1 ? mult + "-pack" + (qty > 1 ? " × " + qty : "") : "vial" + (qty > 1 ? " × " + qty : "")}</small>
            </p>
            <p style={shipNoteStyle}>Free shipping on orders $200+</p>

            <div style={bulkTableStyle}>
              <table style={{width:"100%", borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={tableTh}>Vials</th>
                  <th style={tableTh}>Quantity</th>
                  <th style={tableTh}>Discount</th>
                </tr></thead>
                <tbody>
                  <tr><td style={tableTd}><strong>Most Popular</strong></td><td style={tableTd}>3-pack</td><td style={{...tableTd, color:"#15803d", fontWeight:700}}>10% off</td></tr>
                  <tr><td style={tableTd}><strong>Max Savings</strong></td><td style={tableTd}>5-pack</td><td style={{...tableTd, color:"#15803d", fontWeight:700}}>15% off</td></tr>
                </tbody>
              </table>
            </div>

            {/* Size selector */}
            {product.variants.length > 1 && (
              <>
                <p style={optLabelStyle}>Select Size</p>
                <select value={selectedMg} onChange={e => selectSize(e.target.value)} style={sizeDropdownStyle}>
                  {product.variants.map(v => {
                    const vOnSale = ppIsOnSale(v);
                    const label = vOnSale
                      ? `${v.mg} — $${v.salePrice} (was $${v.price})`
                      : `${v.mg} — $${v.price}`;
                    return <option key={v.mg} value={v.mg}>{label}</option>;
                  })}
                </select>
              </>
            )}

            {/* Pack selector */}
            {product.packs && product.packs.length > 0 && (
              <>
                <p style={optLabelStyle}>Select Pack</p>
                <div style={optRowStyle}>
                  <div onClick={() => setPackIndex(-1)} style={{...optStyle, ...(packIndex === -1 ? optSelectedStyle : {})}} className="rr-opt">Single</div>
                  {product.packs.map((pk, i) => (
                    <div key={i} onClick={() => setPackIndex(i)} style={{...optStyle, ...(packIndex === i ? optSelectedStyle : {}), position:"relative"}} className="rr-opt">
                      {pk.label}
                      {i === 0 && <span className="rr-popular-tag" style={popularTagStyle}>MOST POPULAR</span>}
                      <span style={{...saveTagStyle, color: packIndex === i ? "#7ee390" : "#15803d"}}>{pk.save}% OFF</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Quantity */}
            <p style={optLabelStyle}>Quantity</p>
            <div style={qtyWrapStyle}>
              <button onClick={() => setQty(q => Math.max(1, q-1))} style={qtyBtnStyle}>−</button>
              <div style={qtyNumStyle}>{qty}</div>
              <button onClick={() => setQty(q => q+1)} style={qtyBtnStyle}>+</button>
            </div>

            {/* Add to cart */}
            {outOfStock ? (
              <button disabled style={{...addBtnStyle, background:"#d2d2d7", color:"#86868b", cursor:"not-allowed"}}>
                Out of Stock
              </button>
            ) : (
              <button onClick={handleAdd} style={addBtnStyle}>
                Add to Cart — ${fmt(totalAfterDiscount)}
                {savings > 0 && <span style={{display:"block", fontSize:10, fontWeight:400, opacity:0.8, marginTop:3}}>You save ${fmt(savings)}</span>}
              </button>
            )}

            {/* COA button */}
            {product.coaUrl ? (
              <a href={product.coaUrl} target="_blank" rel="noreferrer" style={coaBtnStyle}>View Certificate of Analysis</a>
            ) : (
              <button onClick={() => onCoaPending(product)} style={coaBtnStyle}>View Certificate of Analysis (Pending)</button>
            )}
          </div>
        </div>
      </div>

      {/* Specs / Disclaimer sections */}
      <div style={fullSectionStyle}>
        <div style={sectionStyle}>
          <p style={sectionLabelStyle}>About</p>
          <p style={sectionPStyle}>{product.desc}</p>
        </div>

        <div style={sectionStyle}>
          <p style={sectionLabelStyle}>Specifications</p>
          <ul style={specsListStyle}>
            <li style={specsLi}><span style={specsK}>Code</span><span style={specsV}>{product.code}</span></li>
            <li style={specsLi}><span style={specsK}>Size</span><span style={specsV}>{selectedMg} per vial</span></li>
            <li style={specsLi}><span style={specsK}>Form</span><span style={specsV}>Lyophilized powder</span></li>
            {product.purity && <li style={specsLi}><span style={specsK}>Purity (HPLC)</span><span style={specsV}>{product.purity}</span></li>}
            <li style={specsLi}><span style={specsK}>Storage</span><span style={specsV}>2-8°C refrigerated</span></li>
            <li style={specsLi}><span style={specsK}>Solubility</span><span style={specsV}>BAC water</span></li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <p style={sectionLabelStyle}>Intended Use</p>
          <p style={sectionPStyle}>This compound is intended <strong>strictly for in-vitro laboratory research</strong> by qualified personnel. It is <strong>not approved for human or veterinary use</strong>.</p>
        </div>

        <div style={sectionStyle}>
          <p style={sectionLabelStyle}>FDA Disclaimer</p>
          <p style={sectionPStyle}>This compound has not been evaluated by the FDA. It is not intended to diagnose, treat, cure, or prevent any disease.</p>
        </div>

        <div style={sectionStyle}>
          <p style={sectionLabelStyle}>Terms of Sale</p>
          <div style={disclaimerBoxStyle}><strong>All sales final.</strong> By purchasing from Roche Research, the buyer affirms they are a qualified researcher.</div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div style={relatedStyle}>
          <h3 style={relatedTitleStyle}>Related Products</h3>
          <div className="rr-rel-grid">
            {related.map(rp => (
              <div key={rp.id} className="rr-rel-card" style={relCardStyle} onClick={() => { if (window.__rrOpenProduct) window.__rrOpenProduct(rp); else { onBack(); setTimeout(() => window.scrollTo(0,0), 50); } }}>
                <div style={relImgStyle}>
                  <VialImage src={rp.variantImages?.[rp.variants[0].mg] || rp.image} alt={rp.name} code={rp.code} style={rp.code === "RR-TR" ? {...relImgImg, maxHeight:160, maxWidth:110} : relImgImg}/>
                </div>
                <p style={relCodeStyle}>{rp.code}</p>
                <p style={relNameStyle}>{rp.name} {rp.variants[0].mg}</p>
                <p style={relPriceStyle}>{ppIsOnSale(rp.variants[0]) ? (<><s style={{color:"#888",fontSize:13,fontWeight:600,marginRight:5}}>${rp.variants[0].price}</s><span style={{color:"#c9962f"}}>${rp.variants[0].salePrice}</span></>) : (<>${rp.variants[0].price}</>)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Zoom modal */}
      {zoomOpen && (
        <div style={zoomModalStyle} onClick={() => setZoomOpen(false)}>
          <button style={zoomCloseStyle} onClick={() => setZoomOpen(false)}>×</button>
          <img src={originalImg} alt={product.name} style={zoomImgStyle} onClick={e => e.stopPropagation()}/>
          {product.variants.length > 1 && (
            <div style={zoomNavStyle} onClick={e => e.stopPropagation()}>
              {product.variants.map(v => (
                <div key={v.mg} onClick={() => setSelectedMg(v.mg)} style={{...zoomThumbStyle, borderColor: selectedMg === v.mg ? "#fff" : "transparent"}}>
                  <img src={product.variantImages?.[v.mg]} alt={v.mg} style={{width:38,height:38,objectFit:"contain"}}/>
                  <span style={zoomThumbLblStyle}>{v.mg}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== STYLES =====
const pageStyle = { background:"#fdfdfb", color:"#111", fontFamily:"-apple-system, BlinkMacSystemFont, sans-serif", minHeight:"100vh", paddingBottom:60, colorScheme:"light only" };
const crumbStyle = { padding:"18px 24px", fontSize:11, color:"#888", letterSpacing:".5px", maxWidth:1400, margin:"0 auto" };
const productSectionStyle = { position:"relative", maxWidth:1400, margin:"0 auto", padding:"0 16px" };
const molBgStyle = { position:"absolute", inset:0, zIndex:0, pointerEvents:"none", width:"100%", height:"100%" };
const imageColStyle = { position:"relative", padding:0, display:"flex", flexDirection:"column", alignItems:"center", zIndex:5 };
const bestTagStyle = { position:"absolute", top:14, left:14, background:"linear-gradient(135deg, #c9962f, #8b6914)", color:"#fff", fontSize:10, fontWeight:800, padding:"6px 14px", borderRadius:20, letterSpacing:".5px", zIndex:5, overflow:"hidden", boxShadow:"0 2px 8px rgba(139,105,20,.3)" };
const zoomBtnStyle = { position:"absolute", top:14, right:14, width:42, height:42, background:"#fff", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,.15)", zIndex:5, border:"none" };
const mainImgWrapStyle = { padding:"30px 0", display:"flex", justifyContent:"center", alignItems:"center", cursor:"zoom-in", width:"100%", minHeight:480 };
const mainImgStyle = { maxWidth:320, width:"100%", height:"auto", display:"block", filter:"drop-shadow(0 18px 30px rgba(0,0,0,0.15))", position:"relative", zIndex:20 };
const thumbsRowStyle = { display:"flex", gap:14, justifyContent:"center", padding:"10px 0 0", flexWrap:"wrap" };
const thumbStyle = { cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", borderBottom:"2px solid transparent", padding:"6px 4px", transition:"all .15s" };
const thumbLabelStyle = { fontSize:10, fontWeight:700, marginTop:4, letterSpacing:".5px" };
const infoColStyle = { padding:"20px 0", position:"relative", zIndex:2 };
const badgesRowStyle = { display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" };
const pillBestStyle = { fontSize:9, padding:"4px 12px", borderRadius:20, fontWeight:700, letterSpacing:".5px", textTransform:"uppercase", background:"linear-gradient(135deg, #c9962f, #8b6914)", color:"#fff", position:"relative", overflow:"hidden", boxShadow:"0 1px 4px rgba(139,105,20,.25)" };
const pillPurityStyle = { fontSize:9, padding:"4px 10px", borderRadius:3, fontWeight:700, letterSpacing:".5px", textTransform:"uppercase", background:"#dcfce7", color:"#166534" };
const pillStockStyle = { fontSize:9, padding:"4px 10px", borderRadius:3, fontWeight:700, letterSpacing:".5px", textTransform:"uppercase", background:"#fef3c7", color:"#92400e" };
const pillSaleStyle = { fontSize:9, padding:"4px 12px", borderRadius:20, fontWeight:700, letterSpacing:".5px", textTransform:"uppercase", background:"linear-gradient(135deg, #c9962f, #8b6914)", color:"#fff", boxShadow:"0 1px 4px rgba(139,105,20,.25)" };
const codeStyle = { fontSize:11, color:"#888", letterSpacing:"1.5px", marginBottom:6, fontWeight:600, textTransform:"uppercase" };
const titleStyle = { fontSize:36, fontWeight:900, margin:"0 0 12px", letterSpacing:"-.5px", fontFamily:"Arial Black, sans-serif", lineHeight:1, color:"#111" };
const subtitleStyle = { fontSize:14, color:"#555", marginBottom:18, lineHeight:1.6, fontStyle:"italic" };
const priceStyle = { fontSize:38, fontWeight:900, marginBottom:4, lineHeight:1, color:"#111" };
const priceSmallStyle = { fontSize:14, color:"#888", fontWeight:400, marginLeft:6 };
const shipNoteStyle = { fontSize:11, color:"#666", marginBottom:22 };
const bulkTableStyle = { margin:"0 0 26px", border:"1px solid #e5e5e5", borderRadius:8, overflow:"hidden", background:"#fff" };
const tableTh = { background:"#f8f8f8", padding:"12px 14px", fontSize:10, letterSpacing:"1px", color:"#666", textAlign:"left", fontWeight:700, textTransform:"uppercase", borderBottom:"1px solid #e5e5e5" };
const tableTd = { padding:14, fontSize:14, borderBottom:"1px solid #f0f0f0" };
const optLabelStyle = { fontSize:11, color:"#888", letterSpacing:"1px", marginBottom:10, fontWeight:700, textTransform:"uppercase", marginTop:14 };
const optRowStyle = { display:"flex", gap:8, marginBottom:20, flexWrap:"wrap", marginTop:14 };
const optStyle = { padding:"12px 20px", fontSize:13, border:"1.5px solid #ddd", cursor:"pointer", fontWeight:600, minWidth:80, textAlign:"center", transition:"all .15s", background:"#fff", color:"#111", position:"relative" };
const optSelectedStyle = { background:"#111", color:"#fff", borderColor:"#111" };
const popularTagStyle = { position:"absolute", top:-10, left:"50%", transform:"translateX(-50%)", background:"linear-gradient(135deg, #c9962f, #8b6914)", color:"#fff", fontSize:8, fontWeight:800, padding:"3px 8px", borderRadius:10, letterSpacing:".5px", whiteSpace:"nowrap", boxShadow:"0 2px 6px rgba(139,105,20,.3)", overflow:"hidden" };
const saveTagStyle = { display:"block", fontSize:9, fontWeight:700, marginTop:2 };
const sizeDropdownStyle = { width:"100%", padding:"14px 40px 14px 16px", border:"1.5px solid #ddd", fontSize:14, fontWeight:600, background:"#fff", cursor:"pointer", appearance:"none", WebkitAppearance:"none", marginBottom:20, fontFamily:"inherit", color:"#111", borderRadius:2, backgroundImage:`url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23111' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat:"no-repeat", backgroundPosition:"right 16px center" };
const qtyWrapStyle = { display:"inline-flex", border:"1.5px solid #ddd", marginBottom:20, borderRadius:2, background:"#fff" };
const qtyBtnStyle = { width:42, height:46, background:"#fff", border:"none", fontSize:18, cursor:"pointer", fontWeight:600, color:"#111" };
const qtyNumStyle = { width:56, height:46, display:"flex", alignItems:"center", justifyContent:"center", borderLeft:"1.5px solid #ddd", borderRight:"1.5px solid #ddd", fontWeight:700, color:"#111" };
const addBtnStyle = { background:"#111", color:"#fff", border:"none", padding:18, width:"100%", fontWeight:800, fontSize:13, letterSpacing:"1.5px", cursor:"pointer", textTransform:"uppercase", borderRadius:2, marginBottom:8 };
const coaBtnStyle = { background:"#fff", color:"#111", border:"1.5px solid #111", padding:14, width:"100%", fontWeight:700, fontSize:12, letterSpacing:"1px", cursor:"pointer", textTransform:"uppercase", borderRadius:2, marginBottom:24, textDecoration:"none", display:"block", textAlign:"center", boxSizing:"border-box" };
const fullSectionStyle = { position:"relative", zIndex:2, maxWidth:1400, margin:"0 auto", padding:"0 24px", background:"#fdfdfb" };
const sectionStyle = { padding:"24px 0", borderTop:"1px solid #eee" };
const sectionLabelStyle = { fontSize:11, color:"#888", letterSpacing:"1.5px", marginBottom:14, fontWeight:700, textTransform:"uppercase" };
const sectionPStyle = { fontSize:14, color:"#555", lineHeight:1.7, marginBottom:10, maxWidth:800 };
const specsListStyle = { listStyle:"none", fontSize:14, maxWidth:800, background:"#fff", borderRadius:8, padding:"14px 20px", border:"1px solid #eee", margin:0 };
const specsLi = { padding:"10px 0", borderBottom:"1px solid #f5f5f5", display:"flex", justifyContent:"space-between" };
const specsK = { color:"#888" };
const specsV = { fontWeight:600, color:"#111" };
const disclaimerBoxStyle = { background:"#fffbeb", border:"1px solid #fde68a", padding:14, borderRadius:6, fontSize:13, color:"#5a4e1a", lineHeight:1.6, maxWidth:800 };
const relatedStyle = { position:"relative", zIndex:2, padding:"36px 12px", borderTop:"1px solid #eee", maxWidth:1400, margin:"0 auto", background:"#fdfdfb", width:"100%", boxSizing:"border-box", overflow:"hidden" };
const relatedTitleStyle = { fontSize:14, fontWeight:700, letterSpacing:"1.5px", marginBottom:22, textTransform:"uppercase", textAlign:"center" };
const relCardStyle = { background:"#fff", border:"1px solid #eee", borderRadius:10, padding:18, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", transition:"all .2s" };
const relImgStyle = { width:"100%", height:200, display:"flex", justifyContent:"center", alignItems:"center", marginBottom:14 };
const relImgImg = { maxWidth:160, maxHeight:200, objectFit:"contain", filter:"drop-shadow(0 8px 16px rgba(0,0,0,0.1))" };
const relCodeStyle = { fontSize:10, color:"#888", letterSpacing:"1.2px", fontWeight:700, marginBottom:4 };
const relNameStyle = { fontSize:14, fontWeight:700, marginBottom:6, color:"#111" };
const relPriceStyle = { fontSize:16, fontWeight:800, color:"#111" };
const zoomModalStyle = { position:"fixed", inset:0, background:"rgba(0,0,0,.92)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 };
const zoomCloseStyle = { position:"absolute", top:20, right:20, width:46, height:46, background:"rgba(255,255,255,.15)", color:"#fff", border:"none", borderRadius:"50%", fontSize:22, cursor:"pointer" };
const zoomImgStyle = { maxWidth:"95%", maxHeight:"90vh", cursor:"zoom-out" };
const zoomNavStyle = { position:"absolute", bottom:30, left:"50%", transform:"translateX(-50%)", display:"flex", gap:10, background:"rgba(255,255,255,.1)", padding:10, borderRadius:30 };
const zoomThumbStyle = { width:58, height:58, borderRadius:"50%", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", border:"2px solid transparent", background:"rgba(255,255,255,.1)" };
const zoomThumbLblStyle = { fontSize:8, color:"#fff", marginTop:1, fontWeight:700 };
